import {
	buildAdminUserSenderRunPath,
	fetchAdminUserSenderAuthJobs,
	fetchAdminUserSenderDispatch,
	type AdminSenderDispatchSummary,
} from '@/api/admin';
import type { SenderAuthJobDetail } from '@/api/sender-panel';
import Pressable from '@/components/motion/pressable';
import { Spinner } from '@/components/ui/spinner';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { ChevronRightIcon, ListIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

interface SenderAuthJobsSubtabProps {
	profileId: number;
	readOnly?: boolean;
	buildRunPath?: (profileId: number, runId: string) => string;
	fetchAuthJobs?: typeof fetchAdminUserSenderAuthJobs;
}

function truncateId(id: string, max = 12): string {
	if (id.length <= max) return id;
	return `${id.slice(0, max)}…`;
}

function AdminSenderRunLink({
	profileId,
	runId,
	label,
	className,
	buildRunPath,
}: {
	profileId: number;
	runId: string;
	label: string;
	className?: string;
	buildRunPath: (profileId: number, runId: string) => string;
}) {
	const navigate = useNavigate();

	return (
		<button
			type='button'
			className={className}
			onClick={() => navigate(buildRunPath(profileId, runId))}
		>
			{label}
		</button>
	);
}

export default function SenderAuthJobsSubtab({
	profileId,
	readOnly = false,
	buildRunPath = buildAdminUserSenderRunPath,
	fetchAuthJobs = fetchAdminUserSenderAuthJobs,
}: SenderAuthJobsSubtabProps) {
	const navigate = useNavigate();
	const [items, setItems] = useState<SenderAuthJobDetail[]>([]);
	const [dispatchItems, setDispatchItems] = useState<AdminSenderDispatchSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const load = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const jobs = await fetchAuthJobs(profileId, { limit: 50 });
			setItems(jobs);
			if (!readOnly) {
				const dispatch = await fetchAdminUserSenderDispatch(profileId, { limit: 50 });
				setDispatchItems(dispatch);
			} else {
				setDispatchItems([]);
			}
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, [profileId, readOnly, fetchAuthJobs]);

	useEffect(() => {
		void load(true);
	}, [load]);

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (error) {
		return (
			<LandingsErrorState
				message='Не удалось загрузить auth jobs.'
				onRetry={() => void load(true)}
			/>
		);
	}

	const hasJobs = items.length > 0;
	const hasDispatch = dispatchItems.length > 0;

	if (!hasJobs && !hasDispatch) {
		return (
			<LandingsEmptyState
				icon={ListIcon}
				title='Auth jobs нет'
				description='У пользователя пока нет auth pipeline jobs и dispatch-очереди.'
			/>
		);
	}

	return (
		<div className='flex flex-col gap-4'>
			{hasJobs ? (
				<div className='flex flex-col gap-2'>
					<p className='text-xs font-medium text-muted-foreground px-1'>Auth jobs</p>
					{items.map(item => (
						<div key={item.id} className='rounded-xl border border-border bg-card px-3 py-3 text-sm'>
							<p className='font-medium'>{item.phoneMasked}</p>
							<p className='text-xs text-muted-foreground mt-1'>
								{item.status} · {item.source}
								{item.linkId ? ` · link ${item.linkId}` : ''}
								{item.clientId ? ` · client ${item.clientId}` : ''}
								{item.landingHostname ? ` · ${item.landingHostname}` : ''}
							</p>
							{item.errorMessage ? (
								<p className='text-xs text-destructive mt-1 break-words'>{item.errorMessage}</p>
							) : null}
							{item.senderRunId ? (
								<p className='text-xs text-muted-foreground font-mono truncate mt-1'>
									run{' '}
									<AdminSenderRunLink
										profileId={profileId}
										runId={item.senderRunId}
										label={item.senderRunId}
										className='text-foreground underline-offset-2 hover:underline'
										buildRunPath={buildRunPath}
									/>
								</p>
							) : null}
							{item.finishedAt ? (
								<p className='text-xs text-muted-foreground mt-1'>
									завершён {new Date(item.finishedAt).toLocaleString('ru-RU')}
								</p>
							) : null}
						</div>
					))}
				</div>
			) : null}

			{!readOnly ? (
			<div className='flex flex-col gap-2'>
				<p className='text-xs font-medium text-muted-foreground px-1'>Очередь dispatch</p>
				{hasDispatch ? (
					dispatchItems.map(item => (
						<Pressable
							key={item.id}
							className='cursor-pointer rounded-xl border border-border bg-card px-3 py-3 text-sm'
							onClick={() => navigate(buildRunPath(profileId, item.runId))}
						>
							<div className='flex items-start justify-between gap-2'>
								<div className='min-w-0 flex-1'>
									<p className='font-medium'>
										{item.status} · {item.source}
									</p>
									<p className='text-xs text-muted-foreground mt-1 font-mono truncate'>
										run {truncateId(item.runId)}
										{item.authJobId ? ` · job ${truncateId(item.authJobId)}` : ''}
										{item.linkId ? ` · link ${item.linkId}` : ''}
									</p>
									<p className='text-xs text-muted-foreground mt-1'>
										{new Date(item.createdAt).toLocaleString('ru-RU')}
									</p>
								</div>
								<ChevronRightIcon size={16} className='text-muted-foreground shrink-0 mt-0.5' />
							</div>
						</Pressable>
					))
				) : (
					<p className='text-sm text-muted-foreground px-1'>Записей в dispatch нет</p>
				)}
			</div>
			) : null}
		</div>
	);
}
